import { useState } from "react"
import { Plus, Pencil, Trash2, Tag, Layers, LayoutGrid } from "lucide-react"
import {
  useGetBusinessCategoriesQuery, useCreateBusinessCategoryMutation, useUpdateBusinessCategoryMutation, useDeleteBusinessCategoryMutation,
  useGetServiceCategoriesQuery, useCreateServiceCategoryMutation, useUpdateServiceCategoryMutation, useDeleteServiceCategoryMutation,
  useGetBusinessTypesQuery, useCreateBusinessTypeMutation, useUpdateBusinessTypeMutation, useDeleteBusinessTypeMutation,
  type AdminBusinessCategory, type AdminServiceCategory, type AdminBusinessType,
} from "../features/admin/adminApi"
import Modal from "../components/Modal"

type Tab = "business" | "service" | "types"

// ── Business Category Form ───────────────────────────────────────────────────
function BizCategoryModal({ initial, onClose }: { initial?: AdminBusinessCategory; onClose: () => void }) {
  const [name, setName] = useState(initial?.name ?? "")
  const [description, setDescription] = useState(initial?.description ?? "")
  const [image, setImage] = useState(initial?.image ?? "")
  const [create] = useCreateBusinessCategoryMutation()
  const [update] = useUpdateBusinessCategoryMutation()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError("")
    try {
      if (initial) await update({ uid: initial.id, name, description, image }).unwrap()
      else await create({ name, description, image }).unwrap()
      onClose()
    } catch (err: any) { setError(err?.data?.message ?? "Failed") }
    finally { setSaving(false) }
  }

  return (
    <Modal title={initial ? "Edit Business Category" : "New Business Category"} onClose={onClose} size="sm">
      {error && <p className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Unicode emoji)</label>
          <div className="flex items-center gap-3">
            <input
              value={image}
              onChange={e => setImage(e.target.value)}
              placeholder="e.g. 🛍️ or paste a single emoji"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {image && (
              <span className="text-3xl leading-none select-none">{image}</span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1">Paste or type a single emoji character to use as the category icon.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Service Category Form ────────────────────────────────────────────────────
function SvcCategoryModal({ initial, onClose }: { initial?: AdminServiceCategory; onClose: () => void }) {
  const [name, setName] = useState(initial?.name ?? "")
  const [iconUrl, setIconUrl] = useState(initial?.iconUrl ?? "")
  const [create] = useCreateServiceCategoryMutation()
  const [update] = useUpdateServiceCategoryMutation()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError("")
    try {
      if (initial) await update({ uid: initial.id, name, iconUrl }).unwrap()
      else await create({ name, iconUrl }).unwrap()
      onClose()
    } catch (err: any) { setError(err?.data?.message ?? "Failed") }
    finally { setSaving(false) }
  }

  return (
    <Modal title={initial ? "Edit Service Category" : "New Service Category"} onClose={onClose} size="sm">
      {error && <p className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Unicode emoji)</label>
          <div className="flex items-center gap-3">
            <input
              value={iconUrl}
              onChange={e => setIconUrl(e.target.value)}
              placeholder="e.g. 🍔 or paste a single emoji"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {iconUrl && (
              <span className="text-3xl leading-none select-none">{iconUrl}</span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1">Paste or type a single emoji character to use as the category icon.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Business Type Form ───────────────────────────────────────────────────────
function BizTypeModal({ initial, onClose }: { initial?: AdminBusinessType; onClose: () => void }) {
  const [title, setTitle] = useState(initial?.title ?? "")
  const [create] = useCreateBusinessTypeMutation()
  const [update] = useUpdateBusinessTypeMutation()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError("")
    try {
      if (initial) await update({ id: initial.id, title }).unwrap()
      else await create({ title }).unwrap()
      onClose()
    } catch (err: any) { setError(err?.data?.message ?? "Failed") }
    finally { setSaving(false) }
  }

  return (
    <Modal title={initial ? "Edit Business Type" : "New Business Type"} onClose={onClose} size="sm">
      {error && <p className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Shared row helper (plain function so key lands directly on the div) ───────
function itemRow(
  key: string | number,
  label: string,
  sub: string | undefined,
  onEdit: () => void,
  onDelete: () => void,
) {
  return (
    <div key={String(key)} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
      </div>
      <div className="flex items-center gap-1 ml-4 flex-shrink-0">
        <button onClick={onEdit} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"><Pencil size={14} /></button>
        <button onClick={onDelete} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
      </div>
    </div>
  )
}

export default function CategoriesPage() {
  const [tab, setTab] = useState<Tab>("business")
  const [modal, setModal] = useState<any>(null)

  const { data: bizCats = [], isLoading: bcLoading } = useGetBusinessCategoriesQuery()
  const [deleteBizCat] = useDeleteBusinessCategoryMutation()

  const { data: svcCats = [], isLoading: scLoading } = useGetServiceCategoriesQuery()
  const [deleteSvcCat] = useDeleteServiceCategoryMutation()

  const { data: bizTypes = [], isLoading: btLoading } = useGetBusinessTypesQuery()
  const [deleteBizType] = useDeleteBusinessTypeMutation()

  const tabs: { key: Tab; label: string; icon: any; count: number }[] = [
    { key: "business", label: "Business Categories", icon: LayoutGrid, count: bizCats.length },
    { key: "service",  label: "Service Categories",  icon: Tag,         count: svcCats.length },
    { key: "types",    label: "Business Types",       icon: Layers,      count: bizTypes.length },
  ]

  const handleDelete = async (fn: () => Promise<any>, name: string) => {
    if (!window.confirm(`Delete "${name}"?`)) return
    try { await fn() } catch (err: any) { alert(err?.data?.message ?? "Cannot delete") }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Categories & Types</h1>
        <button onClick={() => setModal("new")}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
          <Plus size={14} /> New
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {tabs.map(({ key, label, icon: Icon, count }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === key ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}>
            <Icon size={14} />
            {label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === key ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-600"}`}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex justify-end">
          <button onClick={() => setModal("new")}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 font-medium hover:bg-blue-50 rounded-lg">
            <Plus size={13} /> Add
          </button>
        </div>

        {/* Business Categories */}
        {tab === "business" && (
          <div>
            {bcLoading ? (
              <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
            ) : bizCats.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400">No categories yet.</div>
            ) : (
              bizCats.map(cat => itemRow(
                cat.id,
                cat.image ? `${cat.image}  ${cat.name}` : cat.name,
                cat.description || undefined,
                () => setModal({ type: "biz", data: cat }),
                () => handleDelete(() => deleteBizCat(cat.id).unwrap(), cat.name),
              ))
            )}
          </div>
        )}

        {/* Service Categories */}
        {tab === "service" && (
          <div>
            {scLoading ? (
              <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
            ) : svcCats.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400">No categories yet.</div>
            ) : (
              svcCats.map(cat => itemRow(
                cat.id,
                cat.iconUrl ? `${cat.iconUrl}  ${cat.name}` : cat.name,
                undefined,
                () => setModal({ type: "svc", data: cat }),
                () => handleDelete(() => deleteSvcCat(cat.id).unwrap(), cat.name),
              ))
            )}
          </div>
        )}

        {/* Business Types */}
        {tab === "types" && (
          <div>
            {btLoading ? (
              <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
            ) : bizTypes.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400">No types yet.</div>
            ) : (
              bizTypes.map(type => itemRow(
                type.id,
                type.title,
                undefined,
                () => setModal({ type: "bt", data: type }),
                () => handleDelete(() => deleteBizType(type.id).unwrap(), type.title),
              ))
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal === "new" && tab === "business" && <BizCategoryModal onClose={() => setModal(null)} />}
      {modal === "new" && tab === "service"  && <SvcCategoryModal onClose={() => setModal(null)} />}
      {modal === "new" && tab === "types"    && <BizTypeModal onClose={() => setModal(null)} />}
      {modal?.type === "biz" && <BizCategoryModal initial={modal.data} onClose={() => setModal(null)} />}
      {modal?.type === "svc" && <SvcCategoryModal initial={modal.data} onClose={() => setModal(null)} />}
      {modal?.type === "bt"  && <BizTypeModal initial={modal.data} onClose={() => setModal(null)} />}
    </div>
  )
}
